/**
 * MELLY wishlist ("אהבתי"), phase 1: browser-local, no login, no personal data.
 *
 * Storage: localStorage key `melly-wishlist-v1` holds an array of `{ handle, addedAt }` (product handle
 * as the stable key, newest first). Nothing else is stored. A later phase can sync this list to a
 * customer account by reading `MellyWishlist.items()`.
 *
 * Elements:
 * - <melly-wishlist-button data-handle> wraps a <button>; toggles the product and mirrors the state
 *   (aria-pressed, aria-label from data-label-add / data-label-remove).
 * - <melly-wishlist-count> shows the number of liked products (hidden when zero).
 * - <melly-wishlist-view data-root data-section> renders the liked products' cards by requesting
 *   `{root}products/{handle}?section_id={section}` (Section Rendering API), and removes a card as soon
 *   as its product is unliked anywhere.
 *
 * Every change dispatches `melly:wishlist-change` on window; changes made in another tab arrive
 * through the `storage` event, so all hearts, counts and views stay in sync.
 */
const STORAGE_KEY = 'melly-wishlist-v1';
const CHANGE_EVENT = 'melly:wishlist-change';

/** @typedef {{ handle: string, addedAt: number }} WishlistItem */

/** @returns {WishlistItem[]} */
function readItems() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item) => item && typeof item.handle === 'string' && item.handle);
  } catch {
    return [];
  }
}

/** @param {WishlistItem[]} items */
function writeItems(items) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Storage unavailable (private mode, blocked): the state still updates for this page view.
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items } }));
}

export const MellyWishlist = {
  /** @returns {WishlistItem[]} */
  items: () => readItems(),
  /** @param {string} handle */
  has: (handle) => readItems().some((item) => item.handle === handle),
  /** @param {string} handle @returns {boolean} the new liked state */
  toggle(handle) {
    const items = readItems();
    const liked = items.some((item) => item.handle === handle);
    writeItems(liked ? items.filter((item) => item.handle !== handle) : [{ handle, addedAt: Date.now() }, ...items]);
    return !liked;
  },
  /** @param {string} handle */
  remove(handle) {
    writeItems(readItems().filter((item) => item.handle !== handle));
  },
};

window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY) window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items: readItems() } }));
});

class MellyWishlistButton extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    this.querySelector('button')?.addEventListener('click', (event) => {
      event.preventDefault();
      const { handle } = this.dataset;
      if (handle) MellyWishlist.toggle(handle);
    }, { signal });
    window.addEventListener(CHANGE_EVENT, () => this.#render(), { signal });
    this.#render();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  #render() {
    const button = this.querySelector('button');
    if (!button || !this.dataset.handle) return;
    const liked = MellyWishlist.has(this.dataset.handle);
    button.setAttribute('aria-pressed', String(liked));
    const label = liked ? button.dataset.labelRemove : button.dataset.labelAdd;
    if (label) button.setAttribute('aria-label', label);
  }
}

class MellyWishlistCount extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    window.addEventListener(CHANGE_EVENT, () => this.#render(), { signal: this.#controller.signal });
    this.#render();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  #render() {
    const count = readItems().length;
    this.textContent = count ? String(count) : '';
    this.hidden = count === 0;
  }
}

class MellyWishlistView extends HTMLElement {
  #controller = new AbortController();
  /** Handles whose card is shown, in order. */
  #rendered = /** @type {string[]} */ ([]);
  #request = 0;

  connectedCallback() {
    window.addEventListener(CHANGE_EVENT, () => this.#sync(), { signal: this.#controller.signal });
    this.#render();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();
  }

  get #grid() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-wishlist-grid]'));
  }

  /** Unliking removes the card at once; liking (e.g. from another tab) re-renders the list. */
  #sync() {
    const handles = readItems().map((item) => item.handle);
    const grid = this.#grid;
    if (!grid) return;
    let added = false;
    for (const handle of handles) if (!this.#rendered.includes(handle)) added = true;
    for (const card of grid.querySelectorAll('[data-product-handle]')) {
      const handle = /** @type {HTMLElement} */ (card).dataset.productHandle ?? '';
      if (!handles.includes(handle)) card.remove();
    }
    this.#rendered = this.#rendered.filter((handle) => handles.includes(handle));
    if (added) this.#render();
    else this.#updateState();
  }

  async #render() {
    const grid = this.#grid;
    if (!grid) return;
    const request = ++this.#request;
    const handles = readItems().map((item) => item.handle);
    this.dataset.state = handles.length ? 'loading' : 'empty';
    const root = this.dataset.root || '/';
    const section = this.dataset.section || 'melly-product-card';

    const cards = await Promise.all(handles.map(async (handle) => {
      try {
        const response = await fetch(`${root}products/${encodeURIComponent(handle)}?section_id=${section}`);
        if (!response.ok) return null;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        return doc.querySelector('.melly-card');
      } catch {
        return null;
      }
    }));
    if (request !== this.#request) return; // a newer render superseded this one

    grid.replaceChildren(...cards.filter((card) => card !== null));
    this.#rendered = handles.filter((_, index) => cards[index] !== null);
    this.#updateState();
  }

  #updateState() {
    this.dataset.state = this.#rendered.length ? 'ready' : 'empty';
    const count = this.querySelector('[data-wishlist-count]');
    if (count) count.textContent = String(this.#rendered.length);
  }
}

if (!customElements.get('melly-wishlist-button')) customElements.define('melly-wishlist-button', MellyWishlistButton);
if (!customElements.get('melly-wishlist-count')) customElements.define('melly-wishlist-count', MellyWishlistCount);
if (!customElements.get('melly-wishlist-view')) customElements.define('melly-wishlist-view', MellyWishlistView);
